<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <card>
      <tab-rail class="mb-3" :tabs="[{ name: 'Users' }, { name: 'Roles' }]" :value="1" @input="onTab">
        <template #actions>
          <base-button class="d-none d-sm-inline-block" type="primary" size="sm" @click="createRole">
            <i class="tim-icons icon-simple-add mr-1"></i> Create role
          </base-button>
          <base-button class="d-sm-none" type="primary" size="sm" icon title="Create role" @click="createRole">
            <i class="tim-icons icon-simple-add"></i>
          </base-button>
        </template>
      </tab-rail>

      <filter-bar
        :filters="filterSchema"
        :initial-value="filterValues"
        :result-count="totalRows"
        :per-page="perPage"
        :per-page-options="perPageOptions"
        :loading="refreshing"
        saved-filters-key="roles"
        :sort-options="sortOptions"
        :sort-value="sort"
        :show-sort-chip="false"
        @change="onFiltersChanged"
        @update:perPage="onPerPageChange"
        @update:sort="onSortChange"
      >
        <template #actions>
          <base-button v-if="can('download_data')" icon class="btn-link text-white" title="Download CSV" @click="downloadRoles">
            <i class="tim-icons icon-cloud-download-93"></i>
          </base-button>
        </template>
      </filter-bar>

      <data-table
        class="mt-3"
        :columns="columns"
        :rows="roles"
        row-key="rowKey"
        :loading="refreshing"
        :page="currentPage"
        :per-page="perPage"
        :total="totalRows"
        :sort-value="sort"
        :default-sort="{ key: 'type', dir: 'desc' }"
        clickable
        @update:sort="onSortChange"
        @update:page="onPageChange"
        @row-click="editRole"
      >
        <template #cell(name)="{ row: r }">
          <span class="data-table__primary">{{ r.name }}</span>
          <span v-if="r.default_role" class="data-table__tags">
            <span class="badge-tag badge-tag--primary">Built in</span>
          </span>
        </template>
        <template #cell(description)="{ row: r }">
          <span v-if="r.description">{{ r.description }}</span>
          <span v-else class="data-table__muted">{{ r.default_role ? 'Permissions are fixed' : '' }}</span>
        </template>
        <template #cell(time_created)="{ row: r }">
          <span v-if="r.time_created && !r.default_role">{{ formatGroupDate(r.time_created, { dayOfWeek: false }) }}</span>
        </template>
        <template #actions="{ row: r }">
          <el-dropdown>
            <base-button simple type="primary" link size="sm">
              <i class="tim-icons el-icon-more"></i>
            </base-button>
            <template #dropdown>
              <el-dropdown-menu>
                <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="editRole(r)">
                  <i class="tim-icons icon-pencil mr-2"></i> {{ r.default_role ? 'View permissions' : 'Edit role' }}
                </a>
                <a v-if="!r.default_role" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="confirmDelete(r)">
                  <i class="tim-icons icon-trash-simple mr-2"></i> Delete role
                </a>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
        <template #empty>
          <img src="/img/pandas/empty-custom-fields.png" alt="" class="empty-state-img empty-state-img--md" />
          <h4 class="mt-3 empty-state-text">No roles match this search</h4>
          <p class="data-table__muted">The three default roles are always available. Create a custom role to give a team member a specific set of permissions.</p>
        </template>
      </data-table>
    </card>

    <base-alert-dialog
      :show="dialog.show"
      :variant="dialog.variant"
      :title="dialog.title"
      :message="dialog.message"
      :show-cancel="dialog.showCancel"
      :confirm-text="dialog.confirmText"
      :confirm-variant="dialog.confirmVariant"
      @confirm="onDialogConfirm"
      @cancel="dialog.show = false"
    />
  </div>
</template>

<script setup lang="ts">
// Roles list, server paged: GET /companies/{id}/roles?count&offset&q&sort&dir
// (get-company_roles paged mode) returns { roles, total_rows } with default and
// custom roles in one set; `default_role` is 1 for the built-in ones.
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate } from '@/plugins/dates'
import { pageOffset, syncPagingQuery, readPagingQuery, downloadCsv, fetchAllPages } from '@/util/listPage'
import TabRail from '@/components/TabRail.vue'
import type { DataTableColumn } from '@/components/DataTable.vue'

type RoleRow = { id: number; name: string; description: string | null; time_created: string | null; default_role: number; rowKey: string }

// Default and custom roles come from two tables and can share an id.
function withKey(r: any): RoleRow {
  return { ...r, rowKey: (r.default_role ? 'd' : 'c') + r.id }
}

const { proxy } = getCurrentInstance() as any
const companyId = localStorage.getItem('companyId')
const baseUrl = `${API_LOCATION}companies/${companyId}`

const permissions: string[] = JSON.parse(localStorage.getItem('permissions') || '[]')
function can(scope: string) {
  return permissions.includes(scope)
}

const loading = ref(false)
// Starts true so the table shows its skeleton until the first fetch lands.
const refreshing = ref(true)
const roles = ref<RoleRow[]>([])
const totalRows = ref(0)
const perPageOptions = [10, 25, 50, 100]
const perPage = ref(10)
const currentPage = ref(1)
let didInitialFetch = false

function onTab(index: number) {
  if (index === 0) proxy.$router.push('/settings/user-management')
}

const sortOptions = [
  { key: 'type', label: 'Default roles first' },
  { key: 'name', label: 'Name' },
  { key: 'created', label: 'Created' },
]
const sort = ref<{ key: string; dir: 'asc' | 'desc' }>({ key: 'type', dir: 'desc' })

const columns: DataTableColumn[] = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'description', label: 'Description' },
  { key: 'time_created', label: 'Created', sortable: true, sortKey: 'created', width: '160px' },
]

function onSortChange(value: { key: string; dir: 'asc' | 'desc' }) {
  sort.value = value
  currentPage.value = 1
  if (didInitialFetch) fetchRoles()
}

const filterValues = ref<Record<string, any>>({ q: '' })
const filterSchema = computed(() => [
  { key: 'q', type: 'text', label: 'Name or description', placeholder: 'Type to search', operatorLabel: 'contains', primary: true },
])

function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = { ...filterValues.value, ...values }
  if (!didInitialFetch) {
    didInitialFetch = true
    fetchRoles()
    return
  }
  currentPage.value = 1
  fetchRoles()
}

function onPerPageChange(value: number) {
  perPage.value = value
  currentPage.value = 1
  fetchRoles()
}

function onPageChange(page: number) {
  currentPage.value = page
  fetchRoles()
}

function listQuery(count: number, offset: number) {
  const f = filterValues.value
  const params = new URLSearchParams()
  params.set('count', String(count))
  params.set('offset', String(offset))
  if (f.q) params.set('q', String(f.q).trim())
  params.set('sort', sort.value.key)
  params.set('dir', sort.value.dir)
  return `${baseUrl}/roles?${params.toString()}`
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

const dialog = reactive({
  show: false, variant: 'warning', title: '', message: '', showCancel: false,
  confirmText: 'Ok', confirmVariant: 'primary', onConfirm: null as null | (() => void), redirect: '',
})
function showDialog(opts: Partial<typeof dialog>) {
  Object.assign(dialog, { showCancel: false, confirmText: 'Ok', confirmVariant: 'primary', onConfirm: null, redirect: '' }, opts, { show: true })
}
function onDialogConfirm() {
  dialog.show = false
  const action = dialog.onConfirm
  const redirect = dialog.redirect
  dialog.onConfirm = null
  if (action) action()
  else if (redirect) proxy.$router.push(redirect)
}

function handleAuthError(err: any): boolean {
  if (err?.response?.status === 401) {
    proxy.$router.push('/forbidden')
    return true
  }
  return false
}

async function fetchRoles() {
  refreshing.value = true
  try {
    const config = await authConfig()
    const res = await axios.get(listQuery(perPage.value, pageOffset(currentPage.value, perPage.value)), config)
    roles.value = (Array.isArray(res.data.roles) ? res.data.roles : []).map(withKey)
    totalRows.value = Number(res.data.total_rows) || 0
    syncPagingQuery(proxy.$router, proxy.$route, currentPage.value, perPage.value)
  } catch (err: any) {
    if (!handleAuthError(err)) {
      showDialog({ variant: 'danger', title: 'Could not load roles', message: 'Something went wrong. Please refresh and try again.' })
    }
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

async function downloadRoles() {
  try {
    const config = await authConfig()
    const all = await fetchAllPages(async (count, offset) => {
      const res = await axios.get(listQuery(count, offset), config)
      return { rows: Array.isArray(res.data.roles) ? res.data.roles : [], total: Number(res.data.total_rows) || 0 }
    })
    downloadCsv('roles.csv', [
      { label: 'Name', value: (r: RoleRow) => r.name },
      { label: 'Description', value: (r: RoleRow) => r.description || '' },
      { label: 'Type', value: (r: RoleRow) => (r.default_role ? 'Default' : 'Custom') },
      { label: 'Created', value: (r: RoleRow) => (r.time_created ? formatGroupDate(r.time_created, { dayOfWeek: false }) : '') },
    ], all)
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Failed to export roles', timeout: 3000 })
  }
}

function editRole(r: RoleRow) {
  proxy.$router.push({ path: '/settings/roles/edit', query: { id: String(r.id) } })
}

async function createRole() {
  loading.value = true
  try {
    const config = await authConfig()
    const res = await axios.post(`${baseUrl}/roles`, { role: { name: 'New role', description: '' } }, config)
    proxy.$router.push({ path: '/settings/roles/edit', query: { id: String(res.data) } })
  } catch (err: any) {
    if (!handleAuthError(err)) {
      showDialog({ variant: 'danger', title: 'Role not created', message: 'Something went wrong. Please try again.' })
    }
  } finally {
    loading.value = false
  }
}

function confirmDelete(r: RoleRow) {
  showDialog({
    variant: 'danger',
    title: `Delete ${r.name}?`,
    message: 'The role is permanently deleted. Anyone assigned to it loses the access it granted until they are given another role.',
    showCancel: true,
    confirmText: 'Delete role',
    confirmVariant: 'danger',
    onConfirm: () => deleteRole(r),
  })
}

async function deleteRole(r: RoleRow) {
  loading.value = true
  try {
    const config = await authConfig()
    await axios.delete(`${baseUrl}/roles/${r.id}`, config)
    proxy.$notify({ type: 'success', message: `${r.name} deleted`, icon: 'tim-icons icon-check-2' })
    await fetchRoles()
  } catch (err: any) {
    if (!handleAuthError(err)) {
      showDialog({ variant: 'danger', title: 'Role not deleted', message: 'Something went wrong. Please try again.' })
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const paging = readPagingQuery(proxy.$route, 10)
  perPage.value = perPageOptions.includes(paging.perPage) ? paging.perPage : 10
  currentPage.value = paging.page
  setTimeout(() => { if (!didInitialFetch) { didInitialFetch = true; fetchRoles() } }, 800)
})
</script>

<script lang="ts">
import { Dropdown, DropdownMenu, DropdownItem } from 'element-ui'

export default {
  name: 'UserRoles',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
  },
}
</script>
